/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { memo, type MouseEvent } from 'react'
import { HeartOutlined, HeartFilled } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import CommentIcon from './CommentIcon'
import ShareIcon from './ShareIcon'
import ExternalLinkIcon from './ExternalLinkIcon'
import {
  actionButtonStyles,
  linkButtonStyles,
  linkTextStyles,
} from './FeedItem.styles'

/**
 * Like button component for feed items
 * @param isLiked - Whether the item is liked by the current user
 * @param likesCount - Number of likes
 * @param onClick - Click handler function
 */
export const LikeButton = memo<{
  isLiked: boolean
  likesCount: number
  onClick: () => void
}>(({ isLiked, likesCount, onClick }) => (
  <div css={actionButtonStyles} onClick={onClick}>
    {isLiked ? (
      <HeartFilled
        css={css`
          color: ${colors.PRIMARY};
        `}
      />
    ) : (
      <HeartOutlined
        css={css`
          color: ${colors.PRIMARY};
        `}
      />
    )}

    <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
      {likesCount} {likesCount === 1 ? 'Like' : 'Likes'}
    </Text>
  </div>
))

LikeButton.displayName = 'LikeButton'

/**
 * Comment button component for feed items
 * @param commentsCount - Number of comments
 * @param onClick - Click handler function
 */
export const CommentButton = memo<{
  commentsCount?: number
  onClick?: () => void
}>(({ commentsCount, onClick }) => (
  <div css={actionButtonStyles} onClick={onClick}>
    <CommentIcon />
    <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
      {commentsCount} {commentsCount === 1 ? 'Comment' : 'Comments'}
    </Text>
  </div>
))

CommentButton.displayName = 'CommentButton'

/**
 * Share button component for feed items
 * @param onClick - Click handler function
 */
export const ShareButton = memo<{
  onClick: (e: MouseEvent) => void
}>(({ onClick }) => (
  <div
    css={actionButtonStyles}
    onClick={(e) => {
      e.stopPropagation()
      onClick(e)
    }}
  >
    <ShareIcon width={16} height={16} fill={colors.PRIMARY} />
    <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
      Share
    </Text>
  </div>
))

ShareButton.displayName = 'ShareButton'

/**
 * External link button component for feed items
 * @param linkUrl - The external URL
 * @param formattedHost - The formatted host name to display
 */
export const ExternalLinkButton = memo<{
  linkUrl: string
  formattedHost: string
}>(({ linkUrl, formattedHost }) => (
  <a
    href={linkUrl}
    target='_blank'
    rel='noopener noreferrer'
    css={linkButtonStyles}
  >
    <ExternalLinkIcon width={12} height={12} fill={colors.PRIMARY} />
    <Text
      variant='paragraphXSmall'
      weight='bold'
      color={colors.PRIMARY}
      style={linkTextStyles}
    >
      {formattedHost}
    </Text>
  </a>
))

ExternalLinkButton.displayName = 'ExternalLinkButton'
